import React, { useMemo, useState } from 'react'
import { useAppStore } from '../../../../hooks/useAppStore'
import { PageLayout } from '../shared/PageLayout'
import { DataTable } from '../shared/DataTable'
import { SlideOver } from '../shared/SlideOver'
import { ConnectionBadge } from '../shared/ConnectionBadge'
import { StatCard } from '../shared/StatCard'
import { Avatar } from '../shared/Avatar'
import { MasteryBar } from '../shared/MasteryBar'
import { timeAgo } from '../shared/utils'
import {
  SearchIcon,
  UsersIcon,
  SchoolIcon,
  BookOpenIcon,
  Plus,
} from 'lucide-react'
import { Button } from '../../../ui/Button'
function timeAgo(ts: number): string {
  if (!ts) return '—'
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'zojuist'
  if (mins < 60) return `${mins}m geleden`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}u geleden`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d geleden`
  return new Date(ts).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
  })
}
function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  const colors = [
    'bg-indigo-100 text-indigo-700',
    'bg-teal-100 text-teal-700',
    'bg-violet-100 text-violet-700',
    'bg-cyan-100 text-cyan-700',
  ]
  const color = colors[name.charCodeAt(0) % colors.length]
  const sizeClass = size === 'md' ? 'w-10 h-10 text-sm' : 'w-7 h-7 text-xs'
  return (
    <div
      className={`${sizeClass} rounded-full ${color} flex items-center justify-center font-semibold flex-shrink-0`}
    >
      {initials}
    </div>
  )
}
export function TeachersPage() {
  const { accounts, createAccount, getLearnerData } = useAppStore()
  // accounts is AccountsState (object) — extract flat array
  const allAccounts = useMemo(
    () => (accounts?.accountsById ? Object.values(accounts.accountsById) : []),
    [accounts],
  )
  const [search, setSearch] = useState('')
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    null,
  )
  // Add Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [newTeacherName, setNewTeacherName] = useState('')
  const [newTeacherSchool, setNewTeacherSchool] = useState('')
  const [newTeacherEmail, setNewTeacherEmail] = useState('')
  const handleAddTeacher = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTeacherName.trim()) return
    createAccount('teacher', {
      displayName: newTeacherName,
      schoolName: newTeacherSchool,
      email: newTeacherEmail,
    })
    setIsAddModalOpen(false)
    setNewTeacherName('')
    setNewTeacherSchool('')
    setNewTeacherEmail('')
  }
  const teachers = useMemo(
    () => allAccounts.filter((a) => a.role === 'teacher'),
    [allAccounts],
  )
  const learners = useMemo(
    () => allAccounts.filter((a) => a.role === 'learner'),
    [allAccounts],
  )
  const enrichedTeachers = useMemo(() => {
    return teachers.map((teacher) => {
      const schoolLearners = learners.filter(
        (l) =>
          l.schoolName === teacher.schoolName ||
          accounts.relationships?.teacherToLearners?.[teacher.id]?.includes(
            l.id,
          ),
      )
      const lastActive = teacher.lastActive ?? 0
      return {
        ...teacher,
        studentCount: schoolLearners.length,
        lastActive: lastActive,
      }
    })
  }, [teachers, learners, accounts.relationships])
  const filtered = useMemo(() => {
    return enrichedTeachers.filter((t) => {
      return (
        !search ||
        t.displayName.toLowerCase().includes(search.toLowerCase()) ||
        (t.schoolName ?? '').toLowerCase().includes(search.toLowerCase())
      )
    })
  }, [enrichedTeachers, search])
  const selectedTeacher = useMemo(
    () =>
      selectedTeacherId
        ? enrichedTeachers.find((t) => t.id === selectedTeacherId)
        : null,
    [selectedTeacherId, enrichedTeachers],
  )
  const schoolLearners = useMemo(() => {
    if (!selectedTeacher) return []
    return learners
      .filter(
        (l) =>
          l.schoolName === selectedTeacher.schoolName ||
          accounts.relationships?.teacherToLearners?.[
            selectedTeacher.id
          ]?.includes(l.id),
      )
      .map((learner) => {
        const learnerData = getLearnerData(learner.id)
        const familyMastery = learnerData?.familyMastery || {}
        const masteryValues = Object.values(familyMastery).map(
          (m) => m.masteryScore,
        )
        const avgMastery =
          masteryValues.length > 0
            ? Math.round(
                masteryValues.reduce((sum, val) => sum + val, 0) /
                  masteryValues.length,
              )
            : 0
        const totalAttempts = Object.values(familyMastery).reduce(
          (sum, m) => sum + m.attempts,
          0,
        )
        const sessionCount = Math.ceil(totalAttempts / 10)
        return {
          ...learner,
          avgMastery,
          sessionCount,
        }
      })
  }, [selectedTeacher, learners, accounts.relationships, getLearnerData])
  const schools = useMemo(
    () => new Set(teachers.map((t) => t.schoolName).filter(Boolean)).size,
    [teachers],
  )
  const columns = [
    {
      key: 'displayName',
      label: 'Naam',
      render: (v: string, row: (typeof filtered)[0]) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={v} />
          <div>
            <div className="text-sm font-medium text-gray-900">{v}</div>
            <div className="text-xs text-gray-400">{row.groupName ?? '—'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'schoolName',
      label: 'School',
      render: (v: string) => (
        <span className="text-sm text-gray-600">{v ?? '—'}</span>
      ),
    },
    {
      key: 'studentCount',
      label: 'Leerlingen',
      render: (v: number) => (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
          <UsersIcon className="w-3.5 h-3.5 text-gray-400" />
          {v}
        </span>
      ),
    },
    {
      key: 'lastActive',
      label: 'Laatste activiteit',
      render: (v: number) => (
        <span className="text-xs text-gray-500">{timeAgo(v)}</span>
      ),
    },
  ]
  return (
    <PageLayout
      title="Docenten"
      description={`${filtered.length} van ${teachers.length} docenten`}
      actions={
        <div className="flex gap-2">
          <ConnectionBadge status="live" />
          <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            Docent toevoegen
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Totaal docenten"
          value={teachers.length}
          icon={<UsersIcon className="w-5 h-5" />}
          color="indigo"
        />
        <StatCard
          label="Scholen"
          value={schools}
          icon={<SchoolIcon className="w-5 h-5" />}
          color="purple"
        />
        <StatCard
          label="Leerlingen totaal"
          value={learners.length}
          icon={<BookOpenIcon className="w-5 h-5" />}
          color="blue"
        />
      </div>

      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Zoek op naam of school..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-sm pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <UsersIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Geen docenten gevonden</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={(row) => setSelectedTeacherId(row.id)}
        />
      )}

      <SlideOver
        open={!!selectedTeacher}
        onClose={() => setSelectedTeacherId(null)}
        title={selectedTeacher?.displayName ?? ''}
      >
        {selectedTeacher && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
              <Avatar name={selectedTeacher.displayName} size="md" />
              <div>
                <div className="font-semibold text-gray-900">
                  {selectedTeacher.displayName}
                </div>
                <div className="text-sm text-gray-500">
                  {selectedTeacher.schoolName ?? '—'}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Laatste activiteit: {timeAgo(selectedTeacher.lastActive)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-indigo-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-indigo-700">
                  {selectedTeacher.studentCount}
                </div>
                <div className="text-xs text-indigo-600">
                  Leerlingen op school
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-700">
                  {selectedTeacher.schoolName ? 1 : 0}
                </div>
                <div className="text-xs text-purple-600">School</div>
              </div>
            </div>

            {schoolLearners.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Leerlingen op {selectedTeacher.schoolName}
                </h3>
                <div className="space-y-2">
                  {schoolLearners.map((learner) => (
                    <div
                      key={learner.id}
                      className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <Avatar name={learner.displayName} />
                        <div>
                          <div className="text-sm text-gray-800">
                            {learner.displayName}
                          </div>
                          <div className="text-xs text-gray-400">
                            {learner.groupName ?? '—'}
                          </div>
                        </div>
                      </div>
                      <MasteryBar value={learner.avgMastery} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SlideOver>

      {/* Add Teacher Modal */}
      <SlideOver
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Nieuwe docent"
        description="Voeg een docentaccount toe"
      >
        <form onSubmit={handleAddTeacher} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Naam
            </label>
            <input
              type="text"
              required
              value={newTeacherName}
              onChange={(e) => setNewTeacherName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Bijv. Meester Jan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              School (optioneel)
            </label>
            <input
              type="text"
              value={newTeacherSchool}
              onChange={(e) => setNewTeacherSchool(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Naam van de school"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-mailadres (optioneel)
            </label>
            <input
              type="email"
              value={newTeacherEmail}
              onChange={(e) => setNewTeacherEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="docent@school.nl"
            />
          </div>

          <div className="pt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsAddModalOpen(false)}
            >
              Annuleren
            </Button>
            <Button type="submit">Aanmaken</Button>
          </div>
        </form>
      </SlideOver>
    </PageLayout>
  )
}
